import type { AmplifierLocaleId, AmplifierProtocolLanguage } from './amplifierLanguage';
import { amplifierLocales, type AmplifierLocaleKey } from './locales';

const STORAGE_KEY = 'amplifier-language';

export type AmplifierTranslateParams = Record<string, string | number>;

export class AmplifierI18nService {
  private static instance: AmplifierI18nService;
  private currentLanguage: AmplifierLocaleId = 'zh_CN';
  private listeners = new Set<() => void>();
  private pendingProtocolLanguage: AmplifierProtocolLanguage | null = null;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  private constructor() {
    const saved = (
      typeof window === 'undefined'
        ? null
        : localStorage.getItem(STORAGE_KEY)
    ) as AmplifierLocaleId | null;
    if (saved && amplifierLocales[saved]) {
      this.currentLanguage = saved;
    }
  }

  static getInstance(): AmplifierI18nService {
    if (!AmplifierI18nService.instance) {
      AmplifierI18nService.instance = new AmplifierI18nService();
    }
    return AmplifierI18nService.instance;
  }

  getLanguage(): AmplifierLocaleId {
    return this.currentLanguage;
  }

  setLanguage(lang: AmplifierLocaleId): void {
    if (!amplifierLocales[lang] || lang === this.currentLanguage) return;
    this.currentLanguage = lang;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }
    this.notifyListeners();
  }

  /** 用户主动切换后标记，500ms 内忽略相同协议码的心跳回写 */
  markUserLanguageChange(protocol: AmplifierProtocolLanguage): void {
    this.pendingProtocolLanguage = protocol;
    if (this.pendingTimeout) clearTimeout(this.pendingTimeout);
    this.pendingTimeout = setTimeout(() => {
      this.pendingProtocolLanguage = null;
      this.pendingTimeout = null;
    }, 500);
  }

  shouldIgnoreHeartbeatLanguage(protocol: AmplifierProtocolLanguage): boolean {
    return this.pendingProtocolLanguage === protocol;
  }

  t(key: AmplifierLocaleKey, params?: AmplifierTranslateParams): string {
    const locale = amplifierLocales[this.currentLanguage];
    let text: string = locale[key] ?? amplifierLocales.en_US[key] ?? key;
    if (params) {
      for (const [name, value] of Object.entries(params)) {
        text = text.replace(`{${name}}`, String(value));
      }
    }
    return text;
  }

  addListener(listener: () => void): void {
    this.listeners.add(listener);
  }

  removeListener(listener: () => void): void {
    this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export const amplifierI18nService = AmplifierI18nService.getInstance();

export const amplifierT = (
  key: AmplifierLocaleKey,
  params?: AmplifierTranslateParams,
): string => amplifierI18nService.t(key, params);
